import { type ColumnDef, TextLink } from '@olytic/foundation';
import { Link } from 'react-router-dom';

import { type Labels, tableColumns } from './listState';
import type { ObjectDef } from './objectDefs';
import type { ObjectMeta, ObjectRow } from './objectsApi';
import { recordHref } from './routes';

/**
 * Renders each lookup column (`ColumnDef.link`) as a link to the parent's
 * record page, through the library's TextLink around the router's Link, for a
 * table drawn with ListView or RelatedListPage. ObjectListView and
 * ObjectRecordPage do this themselves; ListView does not (yet), so the list
 * pages apply it here. A row without the id shows the name as plain text.
 */
export function withLookupLinks<T>(columns: ColumnDef<T>[]): ColumnDef<T>[] {
  return columns.map((column) => {
    const link = column.link;
    if (!link || column.primary) return column;
    return {
      ...column,
      render: (row: T, ctx: { rowIndex: number }) => {
        const values = row as Record<string, unknown>;
        const shown = column.render ? column.render(row, ctx) : display(column.get ? column.get(row) : values[column.key]);
        const id = values[link.idKey];
        const href = typeof id === 'string' ? recordHref(link.object, id) : null;
        if (href === null) return shown;
        return (
          <TextLink asChild>
            {/* The record link owns the click: a row or card around it must not also open. */}
            <Link to={href} onClick={(event) => event.stopPropagation()}>
              {shown}
            </Link>
          </TextLink>
        );
      },
    };
  });
}

/** A web address worth linking: http(s)://…, or www.… (opened as https). Null otherwise. */
export function webHref(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (/^https?:\/\/\S+$/i.test(text)) return text;
  if (/^www\.\S+\.\S+$/i.test(text)) return `https://${text}`;
  return null;
}

/**
 * Renders a cell whose value is a web address (a LinkedIn profile, a website) as a link that
 * opens it in a new tab. Lookup, name and custom-rendered columns are left as they are.
 */
export function withUrlLinks<T>(columns: ColumnDef<T>[]): ColumnDef<T>[] {
  return columns.map((column) => {
    if (column.link || column.primary || column.render) return column;
    return {
      ...column,
      render: (row: T) => {
        const value = column.get ? column.get(row) : (row as Record<string, unknown>)[column.key];
        const href = webHref(value);
        if (href === null) return display(value);
        return (
          <TextLink href={href} target="_blank" rel="noreferrer noopener" onClick={(event) => event.stopPropagation()}>
            {String(value).trim()}
          </TextLink>
        );
      },
    };
  });
}

function display(value: unknown): string {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

/** A list's columns: the definition's, then every other column /meta lists (hidden until picked), lookups and web addresses linked. */
export function listColumns(def: ObjectDef, meta: ObjectMeta | null, labels: Labels): ColumnDef<ObjectRow>[] {
  return withUrlLinks(withLookupLinks([...def.columns, ...tableColumns(def, meta, labels)]));
}
